import { useBaseStandardUi } from '../../lib/hooks/useBaseStandardUi';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ChevronRight,
  Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useEmbedStore } from '../../lib/embedStore';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { supabase, leaveLeagueAssignGuardian } from '../../lib/supabase';
import { getLeagueSettings } from '../../lib/api/leagueSettings';
import { getStandings, getStandingsWindow } from '../../lib/api/standings';
import { getGroupedTradeMessages } from '../../lib/api/tradeMessages';
import type { GroupedTrade } from '../../lib/utils/tradeUtils';
import { translate } from '../../lib/translations';
import { TeamLogo } from '../TeamLogo/TeamLogo';
import { PushNotificationToggle } from './PushNotificationToggle';
import LnhNewsSection from '../League/LnhNewsSection';
import { MyOfficeEmbed, MyOfficeEmbedLoading } from './MyOfficeEmbed';
import {
  compactCurrency,
  getDaysUntil,
  getPoints,
  getRelativeDateLabel,
  longDate,
} from './myOfficeShared';
import type { StandingRow, TodoItem, TradePreview, UpcomingEventItem } from './myOfficeShared';

interface LeagueSettingsLite {
  max_salary_cap?: number | null;
  max_salary_cap_reserve?: number | null;
  max_signing?: number | null;
  max_injury?: number | null;
  trade_deadline?: string | null;
  deadline_demotion_promotion?: string | null;
  protectionlist?: boolean | null;
  maxdate_protectionlist?: string | null;
  draftMode?: boolean | null;
  pool_start_date?: string | null;
  message_acceuil?: string | null;
  number_foward?: number | null;
  number_def?: number | null;
  number_goalie?: number | null;
  number_reserve?: number | null;
}

interface PlayerCountPerTeam {
  number_of_fowards?: number | null;
  number_of_defenders?: number | null;
}

interface GoalieCountPerTeam {
  number_of_goalie?: number | null;
  number_of_goalie_reserve?: number | null;
}

const parseDateValue = (value?: string | null): Date | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const isNoRowsError = (error: unknown) =>
  typeof error === 'object' && error !== null && 'code' in error && (error as { code?: string }).code === 'PGRST116';

const getUrgencyClass = (days: number | null) => {
  if (days === null) return 'text-muted';
  if (days <= 1) return 'text-danger';
  if (days <= 3) return 'text-warning';
  return 'text-success';
};

const summarizeAssets = (trade: GroupedTrade, side: 'sender' | 'receiver') => {
  const assets: string[] = [];
  trade.trades.forEach((line) => {
    const player = side === 'sender' ? line.sender_player_name : line.receiver_player_name;
    const goalie = side === 'sender' ? line.sender_goalie_name : line.receiver_goalie_name;
    const pick = side === 'sender' ? line.sender_pick_description : line.receiver_pick_description;
    if (player) assets.push(player);
    if (goalie) assets.push(goalie);
    if (pick) assets.push(`🎯 ${pick}`);
  });

  if (assets.length === 0) return 'Aucun actif';
  if (assets.length <= 2) return assets.join(' + ');
  return `${assets.slice(0, 2).join(' + ')} +${assets.length - 2}`;
};

const buildUpcomingEvents = (
  settings: LeagueSettingsLite | null,
  isBuildPhase: boolean,
  language: 'fr' | 'en'
): UpcomingEventItem[] => {
  const events: UpcomingEventItem[] = [];
  if (isBuildPhase) {
    events.push({
      key: 'build-phase',
      title: language === 'fr' ? 'Construction initiale de votre équipe' : 'Initial team build phase',
      description:
        language === 'fr'
          ? 'Terminez votre alignement initial pour débloquer toutes les fonctionnalités.'
          : 'Complete your initial roster to unlock all features.',
      ctaLabel: language === 'fr' ? 'Bâtir mon équipe' : 'Build my team',
      ctaPath: '/build-my-team',
    });
  }

  const tradeDeadline = parseDateValue(settings?.trade_deadline);
  if (tradeDeadline) {
    events.push({
      key: 'trade-deadline',
      title: language === 'fr' ? 'Date limite des transactions' : 'Trade deadline',
      description:
        language === 'fr'
          ? 'Après cette date, les échanges et signatures sont bloqués.'
          : 'After this date, trades and signings are blocked.',
      date: tradeDeadline,
      ctaLabel: language === 'fr' ? 'Voir les échanges' : 'Open trade center',
      ctaPath: '/trade-player',
    });
  }

  const promotionDeadline = parseDateValue(settings?.deadline_demotion_promotion);
  if (promotionDeadline) {
    events.push({
      key: 'promotion-deadline',
      title: language === 'fr' ? 'Date limite promotions/rétrogradations' : 'Promotion/demotion deadline',
      description:
        language === 'fr'
          ? 'Finalisez vos mouvements d’alignement avant la fermeture.'
          : 'Finalize roster movements before lock.',
      date: promotionDeadline,
      ctaLabel: language === 'fr' ? 'Gérer mon équipe' : 'Manage my team',
      ctaPath: '/my-teams',
    });
  }

  const protectionDeadline = parseDateValue(settings?.maxdate_protectionlist);
  if (settings?.protectionlist && protectionDeadline) {
    events.push({
      key: 'protection-deadline',
      title: language === 'fr' ? 'Date limite liste de protection' : 'Protection list deadline',
      description:
        language === 'fr'
          ? 'Vérifiez votre liste de protection avant la date limite.'
          : 'Review your protection list before deadline.',
      date: protectionDeadline,
      ctaLabel: language === 'fr' ? 'Voir la liste de protection' : 'Open protection list',
      ctaPath: '/protection-list',
    });
  }

  if (settings?.draftMode) {
    events.push({
      key: 'draft-live',
      title: language === 'fr' ? 'Repêchage en cours' : 'Draft in progress',
      description:
        language === 'fr'
          ? 'Surveillez vos tours et faites vos sélections rapidement.'
          : 'Monitor your turns and pick quickly.',
      ctaLabel: language === 'fr' ? 'Aller au repêchage' : 'Go to draft',
      ctaPath: '/draft',
    });
  }

  const seasonStart = parseDateValue(settings?.pool_start_date);
  const seasonStartDaysUntil = getDaysUntil(seasonStart);
  if (seasonStart && seasonStartDaysUntil !== null && seasonStartDaysUntil >= 0) {
    events.push({
      key: 'season-start',
      title: language === 'fr' ? 'Début du suivi de saison' : 'Season tracking starts',
      description:
        language === 'fr'
          ? 'Les points du classement total seront calculés à partir de cette date.'
          : 'Total standings points are tracked from this date.',
      date: seasonStart,
      ctaLabel: language === 'fr' ? 'Voir le classement' : 'View standings',
      ctaPath: '/standings',
    });
  }

  return events.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return a.date.getTime() - b.date.getTime();
  });
};

export function MyOffice() {
  usePageTracking('myoffice');

  const navigate = useNavigate();
  const leaveModalRef = useRef<HTMLDivElement | null>(null);
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const setSelectedLeague = useLeagueStore((state) => state.setSelectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const setCurrentLeagueId = useUserStore((state) => state.setCurrentLeagueId);
  const username = useUserStore((state) => state.username);
  const isBpmSession = useUserStore((state) => state.is_bpm_session);
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  const { language } = useLanguageStore();
  const { isStandardFlexible, isBuildPhase } = useLeagueMode();
  const isBaseStandardUi = useBaseStandardUi(isStandardFlexible);

  const [loading, setLoading] = useState(true);
  const [isCommissioner, setIsCommissioner] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [pendingTradesReceived, setPendingTradesReceived] = useState(0);
  const [pendingTradesSent, setPendingTradesSent] = useState(0);
  const [pendingJoinRequests, setPendingJoinRequests] = useState(0);
  const [mainCap, setMainCap] = useState<number>(0);
  const [reserveCap, setReserveCap] = useState<number>(0);
  const [injuredCount, setInjuredCount] = useState(0);
  const [ltirCount, setLtirCount] = useState(0);
  const [userSigningsUsed, setUserSigningsUsed] = useState(0);
  const [teamLogoAbbrev, setTeamLogoAbbrev] = useState<string | null>(null);
  const [standingsRows, setStandingsRows] = useState<StandingRow[]>([]);
  const [currentRow, setCurrentRow] = useState<StandingRow | null>(null);
  const [leagueSettings, setLeagueSettings] = useState<LeagueSettingsLite | null>(null);
  const [tradePreviewsReceived, setTradePreviewsReceived] = useState<TradePreview[]>([]);
  const [tradePreviewsSent, setTradePreviewsSent] = useState<TradePreview[]>([]);
  const [lineupCounts, setLineupCounts] = useState<{ forwards: number; defenders: number; goalies: number; reserveTotal: number } | null>(null);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isLeavingLeague, setIsLeavingLeague] = useState(false);

  useEffect(() => {
    if (showLeaveModal && leaveModalRef.current) {
      leaveModalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showLeaveModal]);

  useEffect(() => {
    if (!selectedLeague?.id) {
      navigate('/dashboard');
      return;
    }

    setCurrentLeagueId(selectedLeague.id);
  }, [selectedLeague?.id, setCurrentLeagueId, navigate]);

  useEffect(() => {
    const leagueId = selectedLeague?.id;
    const userId = Number(currentUserId);
    if (!leagueId || !userId) return;

    let mounted = true;

    const loadDashboard = async () => {
      setLoading(true);
      try {
        const [
          commissionerRes,
          settingsRes,
          mainCapRes,
          reserveCapRes,
          mainLineupPlayersRes,
          nhlInjuredPlayersRes,
          ltirCountRes,
          logoRes,
          tradesRes,
          playerCountsRes,
          goalieCountsRes,
        ] = await Promise.all([
          supabase
            .from('leagues')
            .select('admin_user_id, admin_user_id2')
            .eq('league_id', leagueId)
            .single(),
          getLeagueSettings(leagueId),
          supabase
            .from('get_salary_cap_per_team')
            .select('salary_cap')
            .eq('league_id', leagueId)
            .eq('user_id', userId)
            .eq('isnhl', true)
            .single(),
          supabase
            .from('get_salary_cap_per_team')
            .select('salary_cap')
            .eq('league_id', leagueId)
            .eq('user_id', userId)
            .eq('isnhl', false)
            .single(),
          // Active main lineup player IDs (not yet on LTIR) — to cross-check with NHL injury table
          supabase
            .from('league_players_user')
            .select('player_id')
            .eq('league_id', leagueId)
            .eq('user_id', userId)
            .eq('is_injured', false)
            .eq('isnhl', true),
          // All NHL-injured player IDs from injury_player table
          supabase
            .from('injury_player')
            .select('playerid'),
          // Injured players on LTIR (is_injured = true)
          supabase
            .from('league_players_user')
            .select('player_id', { count: 'exact', head: true })
            .eq('league_id', leagueId)
            .eq('user_id', userId)
            .eq('is_injured', true),
          supabase
            .from('leagues_users')
            .select('nhl_team_abbrev, number_of_sigining_used')
            .eq('league_id', leagueId)
            .eq('user_id', userId)
            .single(),
          getGroupedTradeMessages(leagueId, userId, { page: 1, pageSize: 20 }),
          supabase
            .from('get_number_type_player_per_team')
            .select('number_of_fowards, number_of_defenders')
            .eq('league_id', leagueId)
            .eq('user_id', userId)
            .single(),
          supabase
            .from('get_number_type_goalie_per_team')
            .select('number_of_goalie, number_of_goalie_reserve')
            .eq('league_id', leagueId)
            .eq('user_id', userId)
            .single(),
        ]);

        if (!mounted) return;

        let isComm = false;
        if (!commissionerRes.error && commissionerRes.data) {
          const commissionerData = commissionerRes.data as { admin_user_id?: number; admin_user_id2?: number };
          isComm = commissionerData.admin_user_id === userId || commissionerData.admin_user_id2 === userId;
          setIsCommissioner(isComm);
        }

        const settingsData = (settingsRes.data || null) as LeagueSettingsLite | null;
        setLeagueSettings(settingsData);
        setWelcomeMessage(settingsData?.message_acceuil || '');

        const currentMainCap = mainCapRes.error && !isNoRowsError(mainCapRes.error) ? 0 : mainCapRes.data?.salary_cap || 0;
        const currentReserveCap = reserveCapRes.error && !isNoRowsError(reserveCapRes.error) ? 0 : reserveCapRes.data?.salary_cap || 0;
        setMainCap(currentMainCap);
        setReserveCap(currentReserveCap);

        setInjuredCount(
          (() => {
            const mainLineupIds = new Set(
              (mainLineupPlayersRes.data || []).map((r: { player_id: number }) => r.player_id)
            );
            const nhlInjuredIds = new Set(
              (nhlInjuredPlayersRes.data || []).map((r: { playerid: number }) => r.playerid)
            );
            let count = 0;
            mainLineupIds.forEach((id) => { if (nhlInjuredIds.has(id)) count++; });
            return count;
          })()
        );
        setLtirCount(ltirCountRes.count || 0);
        const userLeagueData = (logoRes.data as {
          nhl_team_abbrev?: string | null;
          number_of_sigining_used?: number | null;
        } | null);
        setTeamLogoAbbrev(userLeagueData?.nhl_team_abbrev ?? null);
        setUserSigningsUsed(userLeagueData?.number_of_sigining_used ?? 0);
        const playerCountsData = (playerCountsRes.error && !isNoRowsError(playerCountsRes.error)
          ? null
          : playerCountsRes.data) as PlayerCountPerTeam | null;
        const goalieCountsData = (goalieCountsRes.error && !isNoRowsError(goalieCountsRes.error)
          ? null
          : goalieCountsRes.data) as GoalieCountPerTeam | null;
        setLineupCounts({
          forwards: playerCountsData?.number_of_fowards || 0,
          defenders: playerCountsData?.number_of_defenders || 0,
          goalies: goalieCountsData?.number_of_goalie || 0,
          reserveTotal:
            (playerCountsData?.number_of_fowards_reserve || 0) +
            (playerCountsData?.number_of_defenders_reserve || 0) +
            (goalieCountsData?.number_of_goalie_reserve || 0),
        });

        const groupedTrades = (tradesRes.data || []) as GroupedTrade[];
        const receivedTrades = groupedTrades.filter((trade) => trade.receiver_user_id === userId);
        const sentTrades = groupedTrades.filter((trade) => trade.sender_user_id === userId);
        setPendingTradesReceived(receivedTrades.length);
        setPendingTradesSent(sentTrades.length);

        setTradePreviewsReceived(
          receivedTrades.slice(0, 3).map((trade) => ({
            key: `received-${trade.trades_id}`,
            counterpart: trade.sender_username,
            sentSummary: summarizeAssets(trade, 'sender'),
            receivedSummary: summarizeAssets(trade, 'receiver'),
            createdAt: trade.time_stamp,
          }))
        );

        setTradePreviewsSent(
          sentTrades.slice(0, 3).map((trade) => ({
            key: `sent-${trade.trades_id}`,
            counterpart: trade.receiver_username,
            sentSummary: summarizeAssets(trade, 'sender'),
            receivedSummary: summarizeAssets(trade, 'receiver'),
            createdAt: trade.time_stamp,
          }))
        );

        const standingsWindow = await getStandingsWindow(leagueId, userId, 2, 2);
        let standingsData = (standingsWindow.data || []) as StandingRow[];

        if (!standingsData.length) {
          const fullStandings = await getStandings(leagueId);
          const fullData = (fullStandings.data || []) as StandingRow[];
          standingsData = fullData
            .slice()
            .sort((a, b) => getPoints(b) - getPoints(a))
            .map((row, index) => ({ ...row, rank: index + 1 }));
        }

        setStandingsRows(standingsData);
        const foundCurrent = standingsData.find((row) => Number(row.user_id) === userId) || null;
        setCurrentRow(foundCurrent);

        if (isComm) {
          const { count, error } = await supabase
            .from('league_invitation')
            .select('*', { count: 'exact', head: true })
            .eq('league_id', leagueId);

          if (error) {
            console.error('Erreur chargement demandes adhésion:', error);
            if (mounted) setPendingJoinRequests(0);
          } else if (mounted) {
            setPendingJoinRequests(count || 0);
          }
        } else {
          setPendingJoinRequests(0);
        }
      } catch (error) {
        console.error('Erreur My Office dashboard:', error);
        toast.error(language === 'fr' ? 'Impossible de charger le tableau de bord.' : 'Failed to load dashboard.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadDashboard();

    return () => {
      mounted = false;
    };
  }, [selectedLeague?.id, currentUserId, language]);

  const upcomingEvents = useMemo(
    () => buildUpcomingEvents(leagueSettings, isBuildPhase, language),
    [leagueSettings, isBuildPhase, language]
  );

  const getInjuryLimit = () => {
    return leagueSettings?.max_injury != null ? leagueSettings.max_injury : 5;
  };

  const nearbyStandings = useMemo(() => {
    if (!standingsRows.length || !currentRow) return standingsRows.slice(0, 5);
    const sorted = standingsRows
      .slice()
      .sort((a, b) => (a.rank || 999) - (b.rank || 999));
    const index = sorted.findIndex((row) => Number(row.user_id) === Number(currentRow.user_id));
    if (index === -1) return sorted.slice(0, 5);
    const start = Math.max(0, index - 1);
    return sorted.slice(start, start + 3);
  }, [standingsRows, currentRow]);

  const availableMainCap = (leagueSettings?.max_salary_cap || 0) - (mainCap || 0);
  const availableReserveCap = (leagueSettings?.max_salary_cap_reserve || 0) - (reserveCap || 0);
  const maxSignings = leagueSettings?.max_signing != null ? Number(leagueSettings.max_signing) : 15;
  const signingsUsageClass =
    maxSignings > 0 && userSigningsUsed >= maxSignings
      ? 'text-danger'
      : maxSignings > 0 && userSigningsUsed >= Math.max(maxSignings - 2, 0)
      ? 'text-warning'
      : 'text-success';
  const signingsBorderColor =
    maxSignings > 0 && userSigningsUsed >= maxSignings
      ? '--bs-danger'
      : maxSignings > 0 && userSigningsUsed >= Math.max(maxSignings - 2, 0)
      ? '--bs-warning'
      : '--bs-success';

  const hasLineupIssues = (() => {
    if (!lineupCounts) return false;
    const requiredForwards = leagueSettings?.number_foward ?? 9;
    const requiredDefenders = leagueSettings?.number_def ?? 6;
    const requiredGoalies = leagueSettings?.number_goalie ?? 3;
    const requiredReserve = leagueSettings?.number_reserve ?? 0;
    const requiredMainTotal = requiredForwards + requiredDefenders + requiredGoalies;
    const currentMainTotal = (lineupCounts.forwards || 0) + (lineupCounts.defenders || 0) + (lineupCounts.goalies || 0);
    if (currentMainTotal !== requiredMainTotal) return true;
    if (lineupCounts.forwards !== requiredForwards) return true;
    if (lineupCounts.defenders !== requiredDefenders) return true;
    if (lineupCounts.goalies !== requiredGoalies) return true;
    if (lineupCounts.reserveTotal !== requiredReserve) return true;
    return false;
  })();

  const teamStatus =
    availableMainCap < 0 || availableReserveCap < 0
      ? language === 'fr'
        ? 'À corriger rapidement'
        : 'Needs immediate fixes'
      : hasLineupIssues
      ? language === 'fr'
        ? 'Alignement invalide : ajustement requis'
        : 'Invalid lineup: action required'
      : pendingTradesReceived > 0
      ? language === 'fr'
        ? 'Décisions en attente'
        : 'Decisions pending'
      : language === 'fr'
      ? 'Stable'
      : 'Stable';

  const todoItems = useMemo<TodoItem[]>(() => {
    const items: TodoItem[] = [];

    // During build phase (standard_flexible + club incomplete), show "Voir les règles" first
    if (isBuildPhase) {
      items.push({
        key: 'view-rules',
        title: language === 'fr' ? 'Voir les règles du pool' : 'View pool rules',
        subtitle:
          language === 'fr'
            ? 'Consultez les règles avant de compléter votre équipe.'
            : 'Review the rules before completing your team.',
        level: 'medium',
        path: '/league-rules',
        cta: language === 'fr' ? 'Règles/Infos Pool' : 'Custom rules',
      });
    }

    if (pendingTradesReceived > 0) {
      items.push({
        key: 'pending-trades',
        title:
          language === 'fr'
            ? `${pendingTradesReceived} offre${pendingTradesReceived > 1 ? 's' : ''} d’échange à répondre`
            : `${pendingTradesReceived} trade offer(s) pending response`,
        subtitle:
          language === 'fr' ? 'Un DG attend votre décision.' : 'A GM is waiting for your decision.',
        level: 'high',
        path: '/messages',
        cta: language === 'fr' ? 'Voir mes échanges' : 'Open my trades',
      });
    }

    // If at least one injured player is in the main alignment, surface a todo
    if (injuredCount > 0) {
      items.push({
        key: 'injured-main',
        title: language === 'fr' ? 'Un joueur de votre alignement est blessé' : 'A player in your lineup is injured',
        subtitle:
          language === 'fr'
            ? "Vous devriez peut-être le mettre sur la liste des blessés et le remplacer par un agent libre."
            : 'You may want to move them to injured reserve and replace with a free agent.',
        level: 'high',
        path: '/my-teams',
        cta: language === 'fr' ? 'Gérer mon équipe' : 'Manage my team',
      });
    }

    if (isCommissioner && pendingJoinRequests > 0) {
      items.push({
        key: 'join-requests',
        title:
          language === 'fr'
            ? `${pendingJoinRequests} demande${pendingJoinRequests > 1 ? 's' : ''} d’adhésion`
            : `${pendingJoinRequests} join request(s) pending`,
        subtitle: language === 'fr' ? 'Action commissaire requise.' : 'Commissioner action required.',
        level: 'high',
        path: '/messages',
        cta: language === 'fr' ? 'Traiter les demandes' : 'Review requests',
      });
    }

    if ((leagueSettings?.max_salary_cap || 0) > 0 && availableMainCap < 0) {
      items.push({
        key: 'main-cap',
        title:
          language === 'fr'
            ? `Plafond principal dépassé de ${compactCurrency(Math.abs(availableMainCap))}`
            : `Main salary cap exceeded by ${compactCurrency(Math.abs(availableMainCap))}`,
        subtitle:
          language === 'fr'
            ? 'Ajustez votre alignement immédiatement.'
            : 'Adjust your roster immediately.',
        level: 'high',
        path: '/my-teams',
        cta: language === 'fr' ? 'Corriger maintenant' : 'Fix now',
      });
    }

    if ((leagueSettings?.max_salary_cap_reserve || 0) > 0 && availableReserveCap < 0) {
      items.push({
        key: 'reserve-cap',
        title:
          language === 'fr'
            ? `Plafond réserve dépassé de ${compactCurrency(Math.abs(availableReserveCap))}`
            : `Reserve cap exceeded by ${compactCurrency(Math.abs(availableReserveCap))}`,
        subtitle:
          language === 'fr' ? 'Réorganisez votre club école.' : 'Rework your reserve roster.',
        level: 'high',
        path: '/my-teams',
        cta: language === 'fr' ? 'Gérer mon équipe' : 'Manage my team',
      });
    }

    const protectionDate = parseDateValue(leagueSettings?.maxdate_protectionlist);
    if (leagueSettings?.protectionlist && protectionDate) {
      const days = getDaysUntil(protectionDate);
      if (days !== null && days >= 0 && days <= 3) {
        items.push({
          key: 'protection-deadline',
          title:
            language === 'fr'
              ? `Liste de protection: ${getRelativeDateLabel(protectionDate, language)}`
              : `Protection list: ${getRelativeDateLabel(protectionDate, language)}`,
          subtitle:
            language === 'fr'
              ? 'Validez votre liste avant la fermeture.'
              : 'Validate your list before lock.',
          level: days <= 1 ? 'high' : 'medium',
          path: '/protection-list',
          cta: language === 'fr' ? 'Compléter maintenant' : 'Complete now',
        });
      }
    }

    const tradeDeadline = parseDateValue(leagueSettings?.trade_deadline);
    if (tradeDeadline) {
      const days = getDaysUntil(tradeDeadline);
      if (days !== null && days >= 0 && days <= 3) {
        items.push({
          key: 'trade-deadline',
          title:
            language === 'fr'
              ? `Date limite des transactions: ${getRelativeDateLabel(tradeDeadline, language)}`
              : `Trade deadline: ${getRelativeDateLabel(tradeDeadline, language)}`,
          subtitle:
            language === 'fr'
              ? 'Préparez vos derniers mouvements.'
              : 'Prepare your final moves.',
          level: days <= 1 ? 'high' : 'medium',
          path: '/trade-player',
          cta: language === 'fr' ? 'Voir les échanges' : 'Open trades',
        });
      }
    }

    if (isBuildPhase) {
      items.push({
        key: 'build-phase',
        title: language === 'fr' ? 'Terminer la construction de votre équipe' : 'Finish building your team',
        subtitle:
          language === 'fr'
            ? 'Votre ligue est en phase de construction initiale.'
            : 'Your league is in initial build phase.',
        level: 'medium',
        path: '/build-my-team',
        cta: language === 'fr' ? 'Bâtir mon équipe' : 'Build my team',
      });
    }

    if (lineupCounts) {
      const requiredForwards = leagueSettings?.number_foward ?? 9;
      const requiredDefenders = leagueSettings?.number_def ?? 6;
      const requiredGoalies = leagueSettings?.number_goalie ?? 3;
      const requiredReserve = leagueSettings?.number_reserve ?? 0;
      const requiredMainTotal = requiredForwards + requiredDefenders + requiredGoalies;
      const currentMainTotal = lineupCounts.forwards + lineupCounts.defenders + lineupCounts.goalies;
      const lineupDiffs: string[] = [];

      if (currentMainTotal !== requiredMainTotal) {
        lineupDiffs.push(language === 'fr'
          ? `Principal ${currentMainTotal}/${requiredMainTotal}`
          : `Main roster ${currentMainTotal}/${requiredMainTotal}`);
      }

      if (lineupCounts.forwards !== requiredForwards) {
        lineupDiffs.push(language === 'fr'
          ? `ATT ${lineupCounts.forwards}/${requiredForwards}`
          : `F ${lineupCounts.forwards}/${requiredForwards}`);
      }
      if (lineupCounts.defenders !== requiredDefenders) {
        lineupDiffs.push(language === 'fr'
          ? `DEF ${lineupCounts.defenders}/${requiredDefenders}`
          : `D ${lineupCounts.defenders}/${requiredDefenders}`);
      }
      if (lineupCounts.goalies !== requiredGoalies) {
        lineupDiffs.push(language === 'fr'
          ? `G ${lineupCounts.goalies}/${requiredGoalies}`
          : `G ${lineupCounts.goalies}/${requiredGoalies}`);
      }
      if (lineupCounts.reserveTotal !== requiredReserve) {
        lineupDiffs.push(language === 'fr'
          ? `Réserve ${lineupCounts.reserveTotal}/${requiredReserve}`
          : `Reserve ${lineupCounts.reserveTotal}/${requiredReserve}`);
      }

      if (lineupDiffs.length > 0) {
        items.push({
          key: 'lineup-invalid',
          title: language === 'fr' ? 'Alignement invalide: ajustement requis' : 'Invalid lineup: adjustment required',
          subtitle: language === 'fr'
            ? `${lineupDiffs.join(' • ')}. Vos points de la soirée peuvent ne pas être comptabilisés.`
            : `${lineupDiffs.join(' • ')}. Your nightly points may not be counted.`,
          level: 'high',
          path: '/my-teams',
          cta: language === 'fr' ? 'Ajuster mon alignement' : 'Adjust my lineup',
        });
      }
    }

    return items;
  }, [
    pendingTradesReceived,
    isCommissioner,
    pendingJoinRequests,
    injuredCount,
    leagueSettings,
    availableMainCap,
    availableReserveCap,
    language,
    isBuildPhase,
    lineupCounts,
  ]);

  const handleConfirmLeave = async () => {
    if (!selectedLeague || !currentUserId) return;
    if (isCommissioner) {
      toast.error(translate('leave.league.error.commissioner', language));
      return;
    }

    const numericUserId = Number(currentUserId);
    if (Number.isNaN(numericUserId)) {
      toast.error(translate('leave.league.error.invalidUser', language));
      return;
    }

    setIsLeavingLeague(true);

    try {
      const result = await leaveLeagueAssignGuardian(selectedLeague.id, numericUserId);

      if (result.success) {
        const successMessage = language === 'fr' && result.message
          ? result.message
          : translate('leave.league.success', language);
        toast.success(successMessage);

        if (result.subscriptionMessage) {
          if (result.subscriptionStatus === 'success') toast.success(result.subscriptionMessage);
          else if (result.subscriptionStatus === 'error') toast.error(result.subscriptionMessage);
          else toast(result.subscriptionMessage);
        }

        setShowLeaveModal(false);
        setSelectedLeague(null);
        setCurrentLeagueId(null);
        navigate('/select-league');
      } else {
        const errorMessage = language === 'fr' && result.error?.message
          ? result.error.message
          : translate('leave.league.error.generic', language);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Unexpected error leaving league:', error);
      toast.error(translate('leave.league.error.unexpected', language));
    } finally {
      setIsLeavingLeague(false);
    }
  };

  if (!selectedLeague) return null;

  if (loading) {
    if (isEmbed) {
      return <MyOfficeEmbedLoading language={language} />;
    }
    return (
      <div className="card shadow-lg border-0">
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="text-muted mt-3 mb-0">{language === 'fr' ? 'Chargement du bureau...' : 'Loading office dashboard...'}</p>
        </div>
      </div>
    );
  }

  // PHASE 2 — rendu embed Arsenal Sports : mêmes données/état, présentation dédiée.
  if (isEmbed) {
    return (
      <MyOfficeEmbed
        language={language}
        leagueName={selectedLeague.name}
        teamName={username || 'DG'}
        teamLogoAbbrev={teamLogoAbbrev}
        teamStatus={teamStatus}
        currentRow={currentRow}
        nearbyStandings={nearbyStandings}
        mainCap={mainCap}
        maxSalaryCap={leagueSettings?.max_salary_cap || 0}
        availableMainCap={availableMainCap}
        lineupCounts={lineupCounts}
        requiredLineup={{
          forwards: leagueSettings?.number_foward ?? 9,
          defenders: leagueSettings?.number_def ?? 6,
          goalies: leagueSettings?.number_goalie ?? 3,
          reserve: leagueSettings?.number_reserve ?? 0,
        }}
        ltirCount={ltirCount}
        injuryLimit={getInjuryLimit()}
        injuredCount={injuredCount}
        signingsUsed={userSigningsUsed}
        maxSignings={maxSignings}
        todoItems={todoItems}
        upcomingEvents={upcomingEvents}
        pendingTradesReceived={pendingTradesReceived}
        pendingTradesSent={pendingTradesSent}
        tradePreviewsReceived={tradePreviewsReceived}
        tradePreviewsSent={tradePreviewsSent}
        isStandardFlexible={isStandardFlexible}
        isBuildPhase={isBuildPhase}
        isBpmSession={Boolean(isBpmSession)}
        isCommissioner={isCommissioner}
        showLeaveModal={showLeaveModal}
        isLeavingLeague={isLeavingLeague}
        onOpenLeaveModal={() => {
          if (isCommissioner) {
            toast.error(translate('leave.league.error.commissioner', language));
            return;
          }
          setShowLeaveModal(true);
        }}
        onCloseLeaveModal={() => setShowLeaveModal(false)}
        onConfirmLeave={handleConfirmLeave}
        onNavigate={navigate}
      />
    );
  }

  const dashboardBodyClass = isEmbed ? 'card-body p-2 p-md-3' : 'card-body p-3 p-md-4 p-xl-5';
  const sectionGridClass = isEmbed ? 'row g-3' : 'row g-4';
  const sectionCardBodyClass = isEmbed ? 'card-body p-3' : 'card-body p-4';
  const shouldPromoteQuickAccess = isStandardFlexible && todoItems.length === 0;

  return (
    <div className="card shadow-lg border-0">
      <div className={dashboardBodyClass}>
        {isEmbed && !isStandardFlexible && (
          <div className="col-12 d-flex justify-content-end pe-2 mb-2">
            <PushNotificationToggle />
          </div>
        )}

        {!isEmbed && (
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
            <div>
              <div className="text-uppercase text-muted small fw-semibold mb-1">{translate('gm.office', language)}</div>
              <h2 className="mb-1 fw-bold">
                {language === 'fr' ? 'Bonjour' : 'Hello'}, {username || 'DG'} 👋
              </h2>
              <p className="mb-0 text-muted fw-semibold">“{selectedLeague.name}”</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <span style={{ backgroundColor: '#ffffff', color: '#000000', border: '1px solid rgba(0,0,0,0.2)', borderRadius: '6px', padding: '6px 12px', fontSize: '0.85rem', fontWeight: 600 }}>
                {language === 'fr' ? 'Classement' : 'Rank'}: #{currentRow?.rank || '—'}
              </span>
              <span style={{ backgroundColor: '#ffffff', color: '#000000', border: '1px solid rgba(0,0,0,0.2)', borderRadius: '6px', padding: '6px 12px', fontSize: '0.85rem', fontWeight: 600 }}>
                {language === 'fr' ? 'Points' : 'Points'}: {getPoints(currentRow)}
              </span>
              <span style={{ backgroundColor: '#ffffff', color: '#000000', border: '1px solid rgba(0,0,0,0.2)', borderRadius: '6px', padding: '6px 12px', fontSize: '0.85rem', fontWeight: 600 }}>
                {language === 'fr' ? 'Cap dispo' : 'Cap space'}: {compactCurrency(availableMainCap)}
              </span>
            </div>
          </div>
        )}

        {!isEmbed && welcomeMessage && (
          <div className="alert alert-info border-0 shadow-sm mb-4">
            <div className="fw-semibold mb-1">📢 {language === 'fr' ? 'Message de la ligue' : 'League message'}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{welcomeMessage}</div>
          </div>
        )}

        <div className={sectionGridClass}>
          {/* Section Accès rapide — mode Standard Flexible uniquement */}
          {isStandardFlexible && (
            <div className={`col-12 ${shouldPromoteQuickAccess ? 'order-1' : 'order-2'} mb-0`}>
              <div className="card border-0 shadow-sm" style={{ borderTop: `3px solid ${isBuildPhase ? 'var(--bs-danger)' : 'var(--bs-warning)'}` }}>
                <div className={`card-body ${isEmbed ? 'p-3' : 'p-3 p-md-4'}`}>
                  <h5 className={`fw-bold ${isEmbed ? 'mb-2' : 'mb-3'}`}>{language === 'fr' ? 'Accès rapide' : 'Quick Access'}</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {isBuildPhase ? (
                      <>
                        <button className="btn btn-danger btn-sm" onClick={() => navigate('/build-my-team')}>
                          🔨 {language === 'fr' ? 'Bâtir mon club' : 'Build My Team'}
                        </button>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/league-rules')}>
                          📘 {language === 'fr' ? ' Règles/Infos Pool' : 'Custom rules'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/my-teams')}>
                          🏒 {language === 'fr' ? 'Mon équipe' : 'My Team'}
                        </button>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/free-agents')}>
                          📋 {language === 'fr' ? 'Agents libres' : 'Free Agents'}
                        </button>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/standings')}>
                          🏆 {language === 'fr' ? 'Classement' : 'Standings'}
                        </button>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/followed-poolers')}>
                          👥 {language === 'fr' ? 'Poolers suivis' : 'Followed poolers'}
                        </button>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/other-teams')}>
                          👀 {language === 'fr' ? 'Voir les autres équipes' : 'View Other Teams'}
                        </button>
                        <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/league-rules')}>
                          📘 {language === 'fr' ? ' Règles/Infos Pool' : 'Custom rules'}
                        </button>
                      </>
                    )}
                  </div>
                  {isEmbed && (
                    <div className="mt-2">
                      <PushNotificationToggle />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={`col-12 col-xl-4 ${shouldPromoteQuickAccess ? 'order-2' : 'order-1'}`}>
            <div className="card h-100 border-0 shadow-sm">
              <div className={sectionCardBodyClass}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="mb-0 fw-bold"> {language === 'fr' ? 'À faire' : 'To do'}</h5>
                  <span className="badge bg-danger-subtle text-danger-emphasis">
                    <span style={{ color: '#000000' }}>{todoItems.length}</span>
                  </span>
                </div>

                {todoItems.length === 0 ? (
                  <p className="text-muted mb-0">
                    {language === 'fr'
                      ? 'Aucune action urgente. Votre organisation est en ordre.'
                      : 'No urgent actions. Your organization is in good shape.'}
                  </p>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {todoItems.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className={`btn text-start border w-100 ${
                          item.level === 'high'
                            ? 'border-danger-subtle bg-danger-subtle'
                            : item.level === 'medium'
                            ? 'border-warning-subtle bg-warning-subtle'
                            : 'border-secondary-subtle bg-body-tertiary'
                        }`}
                        onClick={() => navigate(item.path)}
                      >
                        <div className="fw-semibold">{item.title}</div>
                        <div className="small text-muted mb-2">{item.subtitle}</div>
                        <div className="small fw-semibold text-primary">{item.cta} <ChevronRight size={14} /></div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`col-12 col-xl-8 ${isStandardFlexible ? 'order-3' : 'order-2'}`}>
            <div className="card h-100 shadow-sm" style={{ border: '1px solid var(--bs-border-color)', borderTop: '3px solid var(--bs-primary)' }}>
              <div className={`card-body ${isEmbed ? 'p-3' : 'p-3 p-md-4'}`}>
                <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
                  <div>
                    <h5 className="mb-1 fw-bold">🏒 {language === 'fr' ? 'Mon équipe' : 'My team'}</h5>
                    <p className="text-muted mb-0 small">{language === 'fr' ? 'Vue rapide de votre organisation' : 'Quick team overview'}</p>
                  </div>
                  {teamLogoAbbrev && <TeamLogo abbrev={teamLogoAbbrev} size={56} />}
                </div>

                <div className="row g-2 g-md-3 mb-3">
                  <div className="col-6 col-md-4">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: '4px solid var(--bs-primary)' }}>
                      <div className="text-body-secondary small fw-semibold mb-1">🏆 {language === 'fr' ? 'Position' : 'Rank'}</div>
                      <div className="fw-bold fs-4 text-primary lh-1">#{currentRow?.rank || '—'}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: '4px solid var(--bs-info)' }}>
                      <div className="text-body-secondary small fw-semibold mb-1">⭐ {language === 'fr' ? 'Points' : 'Points'}</div>
                      <div className="fw-bold fs-4 text-info lh-1">{getPoints(currentRow)}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: '4px solid var(--bs-warning)' }}>
                      <div className="text-body-secondary small fw-semibold mb-1">💰 {language === 'fr' ? 'Masse salariale' : 'Salary used'}</div>
                      <div className="fw-bold fs-5 text-warning lh-1">{compactCurrency(mainCap)}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: `4px solid var(${availableMainCap < 0 ? '--bs-danger' : '--bs-success'})` }}>
                      <div className="text-body-secondary small fw-semibold mb-1">📊 {language === 'fr' ? 'Cap disponible' : 'Cap space'}</div>
                      <div className={`fw-bold fs-5 lh-1 ${availableMainCap < 0 ? 'text-danger' : 'text-success'}`}>
                        {compactCurrency(availableMainCap)}
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: `4px solid var(${ltirCount > 0 ? '--bs-warning' : '--bs-secondary'})` }}>
                      <div className="text-body-secondary small fw-semibold mb-1">🩺 {language === 'fr' ? 'Joueurs sur la liste des blessés' : 'Players on injured list'}</div>
                      <div className={`fw-bold fs-4 lh-1 ${ltirCount > 0 ? 'text-warning' : 'text-body-emphasis'}`}>
                        {ltirCount} / {getInjuryLimit()}
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: `4px solid var(${injuredCount > 0 ? '--bs-warning' : '--bs-secondary'})` }}>
                      <div className="text-body-secondary small fw-semibold mb-1">🚑 {language === 'fr' ? 'Blessés (alignement principal)' : 'Injured (main lineup)'}</div>
                      <div className={`fw-bold fs-4 lh-1 ${injuredCount > 0 ? 'text-warning' : 'text-body-emphasis'}`}>{injuredCount}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: `4px solid var(${signingsBorderColor})` }}>
                      <div className="text-body-secondary small fw-semibold mb-1">{language === 'fr' ? 'Signatures agents libres' : 'Free agent signings'}</div>
                      <div className={`fw-bold fs-4 lh-1 ${signingsUsageClass}`}>{userSigningsUsed} / {maxSignings}</div>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div className="small text-muted">
                    {language === 'fr' ? 'Statut général' : 'General status'}: <span className="fw-semibold">{teamStatus}</span>
                  </div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/my-teams')}>
                    {language === 'fr' ? 'Voir mon équipe' : 'View my team'} <ChevronRight size={16} className="ms-1" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {!isStandardFlexible && (
          <div className="col-12 col-xl-7 order-3">
            <div className="card h-100 border-0 shadow-sm">
              <div className={sectionCardBodyClass}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="mb-0 fw-bold">🔄 {language === 'fr' ? 'Mes échanges' : 'My trades'}</h5>
                  <span className="badge bg-primary-subtle text-primary-emphasis">
                    <span style={{ color: '#000000' }}>{pendingTradesReceived + pendingTradesSent}</span>
                  </span>
                </div>

                {(pendingTradesReceived > 0 || pendingTradesSent > 0) ? (
                  <>
                    {pendingTradesReceived > 0 && (
                      <div className="mb-3">
                        <div className="fw-semibold mb-2">📥 {language === 'fr' ? 'Offres reçues' : 'Received offers'}</div>
                        <div className="d-flex flex-column gap-2">
                          {tradePreviewsReceived.map((trade) => (
                            <button
                              key={trade.key}
                              type="button"
                              className="btn btn-outline-danger text-start"
                              onClick={() => navigate('/messages')}
                            >
                              <div className="fw-semibold">{trade.counterpart}</div>
                              <div className="small">{language === 'fr' ? 'Ils offrent' : 'They offer'}: {trade.sentSummary}</div>
                              <div className="small">{language === 'fr' ? 'Contre' : 'For'}: {trade.receivedSummary}</div>
                              <div className="small text-muted mt-1">
                                <Clock size={12} className="me-1" />
                                {new Date(trade.createdAt).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {pendingTradesSent > 0 && (
                      <div className="mb-3">
                        <div className="fw-semibold mb-2">📤 {language === 'fr' ? 'Offres envoyées' : 'Sent offers'}</div>
                        <div className="d-flex flex-column gap-2">
                          {tradePreviewsSent.map((trade) => (
                            <button
                              key={trade.key}
                              type="button"
                              className="btn btn-outline-secondary text-start"
                              onClick={() => navigate('/messages')}
                            >
                              <div className="fw-semibold">{trade.counterpart}</div>
                              <div className="small">{language === 'fr' ? 'Vous offrez' : 'You offer'}: {trade.sentSummary}</div>
                              <div className="small">{language === 'fr' ? 'Contre' : 'For'}: {trade.receivedSummary}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {pendingTradesReceived > 0 && (
                      <div className="alert alert-danger border-0 mb-3">
                        ⚡ {language === 'fr'
                          ? `${pendingTradesReceived} offre${pendingTradesReceived > 1 ? 's' : ''} nécessite${pendingTradesReceived > 1 ? 'nt' : ''} votre attention`
                          : `${pendingTradesReceived} offer(s) need your attention`}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted mb-3">
                    {language === 'fr' ? 'Aucune offre d’échange en attente.' : 'No pending trade offers.'}
                  </p>
                )}

                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => navigate('/messages')}>
                  {language === 'fr' ? 'Voir mes échanges' : 'View my trades'} <ChevronRight size={16} className="ms-1" />
                </button>
              </div>
            </div>
          </div>
          )}

          {upcomingEvents.length > 0 && (
          <div className="col-12 col-xl-5 order-4">
            <div className="card h-100 border-0 shadow-sm">
              <div className={sectionCardBodyClass}>
                <h5 className="mb-3 fw-bold">📅 {language === 'fr' ? 'À venir' : 'Upcoming'}</h5>
                <div className="d-flex flex-column gap-3">
                  {upcomingEvents.slice(0, 6).map((event) => {
                    const days = getDaysUntil(event.date);
                    const urgencyClass = getUrgencyClass(days);
                    return (
                      <div key={event.key} className={`border rounded ${isEmbed ? 'p-2' : 'p-3'}`}>
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                          <div className="fw-semibold">{event.title}</div>
                          <span className={`small fw-semibold ${urgencyClass}`}>{getRelativeDateLabel(event.date, language)}</span>
                        </div>
                        <div className="small text-muted mb-2">{longDate(event.date, language)}</div>
                        <div className="small">{event.description}</div>
                        {event.ctaLabel && event.ctaPath && (
                          <button type="button" className="btn btn-link p-0 mt-2 small" onClick={() => navigate(event.ctaPath!)}>
                            {event.ctaLabel} <ChevronRight size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          )}

          <div className="col-12 order-5">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                  <div>
                    <h5 className="mb-1 fw-bold">🏆 {language === 'fr' ? 'Mon classement' : 'My standings'}</h5>
                    <p className="text-muted mb-0">
                      {language === 'fr'
                        ? 'Votre position et les équipes autour de vous'
                        : 'Your rank and nearby teams'}
                    </p>
                  </div>
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => navigate('/standings')}>
                    {language === 'fr' ? 'Voir le classement' : 'View standings'} <ChevronRight size={16} className="ms-1" />
                  </button>
                </div>

                <div className="row g-2 g-md-3 mb-3">
                  <div className="col-6 col-md-3">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: '4px solid var(--bs-primary)' }}>
                      <div className="text-body-secondary small fw-semibold mb-1">{language === 'fr' ? 'Position' : 'Rank'}</div>
                      <div className="fw-bold fs-4 text-primary lh-1">#{currentRow?.rank || '—'}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: '4px solid var(--bs-info)' }}>
                      <div className="text-body-secondary small fw-semibold mb-1">{language === 'fr' ? 'Points' : 'Points'}</div>
                      <div className="fw-bold fs-4 text-info lh-1">{getPoints(currentRow)}</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: '4px solid var(--bs-secondary)' }}>
                      <div className="text-body-secondary small fw-semibold mb-1">{language === 'fr' ? 'Équipe devant' : 'Team ahead'}</div>
                      <div className="fw-semibold text-body-emphasis lh-1">
                        {nearbyStandings.find((row) => (row.rank || 0) === (currentRow?.rank || 0) - 1)?.username || '—'}
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-3 rounded-3 h-100" style={{ background: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)', borderLeft: '4px solid var(--bs-secondary)' }}>
                      <div className="text-body-secondary small fw-semibold mb-1">{language === 'fr' ? 'Équipe derrière' : 'Team behind'}</div>
                      <div className="fw-semibold text-body-emphasis lh-1">
                        {nearbyStandings.find((row) => (row.rank || 0) === (currentRow?.rank || 0) + 1)?.username || '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>{language === 'fr' ? 'Pos' : 'Rank'}</th>
                        <th>{language === 'fr' ? 'Équipe' : 'Team'}</th>
                        <th>{language === 'fr' ? 'Points' : 'Points'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nearbyStandings.map((row) => {
                        const isCurrent = Number(row.user_id) === Number(currentRow?.user_id);
                        return (
                          <tr key={`standing-${row.user_id}`} className={isCurrent ? 'table-primary' : ''}>
                            <td className="fw-semibold">{row.rank || '—'}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {row.nhl_team_abbrev && <TeamLogo abbrev={row.nhl_team_abbrev} size={24} />}
                                <span className={isCurrent ? 'fw-bold' : ''}>{row.username}</span>
                              </div>
                            </td>
                            <td className="fw-semibold">{getPoints(row)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {!isBpmSession && (
            <div className={`col-12 ${isBaseStandardUi ? 'order-last' : 'order-6'}`}>
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="mb-0 fw-bold">📰 {language === 'fr' ? 'Nouvelles' : 'News'}</h5>
                    <a
                      href="https://www.toutsurlehockey.com/"
                      target="_blank"
                      rel="noreferrer noopener"
                      className="btn btn-outline-primary btn-sm"
                    >
                      {language === 'fr' ? 'Voir toutes les nouvelles' : 'View all news'} <ChevronRight size={16} className="ms-1" />
                    </a>
                  </div>
                  <LnhNewsSection compact />
                </div>
              </div>
            </div>
          )}

          {!isEmbed && (
            <div className="col-12 d-flex justify-content-end pe-2">
              <PushNotificationToggle />
            </div>
          )}

        </div>
      </div>

      {!isStandardFlexible && (
        <div className="card-footer bg-transparent border-0 p-4">
          <div className="d-flex flex-column align-items-center w-100">
            <p className="text-muted small mb-2 text-center" style={{ maxWidth: 720 }}>
              {translate('leave.league.description', language)}
            </p>
            <button
              className="btn btn-danger fw-bold w-100"
              style={{ maxWidth: 520 }}
              disabled={isCommissioner}
              onClick={() => {
                if (isCommissioner) {
                  toast.error(translate('leave.league.error.commissioner', language));
                  return;
                }
                setShowLeaveModal(true);
              }}
              title={translate('leave.league.cta.title', language)}
            >
              <AlertTriangle className="me-2" size={18} />
              {translate('leave.league.button', language)}
            </button>
            <small className="text-muted mt-2">
              {translate(isCommissioner ? 'leave.league.note.commissioner' : 'leave.league.note.default', language)}
            </small>
          </div>
        </div>
      )}

      {!isStandardFlexible && showLeaveModal && (
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered" role="document" ref={leaveModalRef}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{translate('leave.league.modal.title', language)}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  disabled={isLeavingLeague}
                  onClick={() => setShowLeaveModal(false)}
                />
              </div>
              <div className="modal-body">
                <p>
                  {translate('leave.league.modal.body', language, {
                    leagueName: selectedLeague?.name ?? '',
                  })}
                </p>
                <p className="text-danger small mb-0">{translate('leave.league.modal.irreversible', language)}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={isLeavingLeague}
                  onClick={() => setShowLeaveModal(false)}
                >
                  {translate('leave.league.modal.cancel', language)}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={isLeavingLeague}
                  onClick={handleConfirmLeave}
                >
                  {isLeavingLeague ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      {translate('leave.league.modal.progress', language)}
                    </>
                  ) : (
                    translate('leave.league.modal.confirm', language)
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </div>
      )}
    </div>
  );
}

